/* global CONFIGURATION, setupNewRelic */

import * as Sentry from "@sentry/browser";
import { Elm } from "./elm-app.js";

// Recording runs against real users, so only a replay may take a shortcut.
const isReplayingRecordedSession = Boolean(window.Meticulous?.isRunningAsTest);

// Meticulous replays serve the raw dist, where the release-tag placeholder
// is never substituted, so release-tagged assets live under /d4c5d27a7cafd33b385f36bd9ebc1429af540cbf/.
if (isReplayingRecordedSession) {
  CONFIGURATION.release = "d4c5d27a7cafd33b385f36bd9ebc1429af540cbf";
}

import PdfPreviewPort from "./Ports/pdf-preview.js";
import "./WebComponents/elm-modal.js";
import "./WebComponents/elm-field.js";
import "./WebComponents/elm-form.js";
import "./WebComponents/elm-tooltip.js";
import "./WebComponents/elm-listbox.js";
import "./WebComponents/elm-announcement.js";
import XmlPreviewPort from "./Ports/xml-preview.js";

const WindowPort = require("./Ports/window.js");
const GooglePlacesPort = require("./Ports/google-places.js");
const GoogleRecaptchaPort = require("./Ports/google-recaptcha.js");
const GoogleRecaptchaReplayPort = require("./Ports/google-recaptcha-replay.js");
const LocalStorage = require("./Ports/local-storage.js");
const AccountTokenStorage = require("./Ports/account-token-storage.js");
const NavigationPort = require("./Ports/navigation.js");
const DocumentLanguagePort = require("./Ports/document-language.js");
const SentryPort = require("./Ports/sentry.js");
const StripePort = require("./Ports/stripe.js");
const ThemePort = require("./Ports/theme.js");
const CustomJavascriptPort = require("./Ports/custom-javascript.js");
const PageTitlePort = require("./Ports/page-title.js");
const PageFocusPort = require("./Ports/page-focus.js");
const FocusElementPort = require("./Ports/focus-element.js");
const RainforestPort = require("./Ports/rainforest.js");
const OnBeforeUnloadPort = require("./Ports/onBeforeUnload.js");
const OnTabSelectPort = require("./Ports/onTabSelect.js");
const IntercomPort = require("./Ports/intercom.js");
const FullStoryPort = require("./Ports/fullstory");
const FaviconPort = require("./Ports/favicon.js");
const IdleTimePort = require("./Ports/idle-time.js");
const ScrollDetectionPort = require("./Ports/scroll-detection.js");
const FileSelectionPort = require("./Ports/file-selection.js");
const FileUploadPort = require("./Ports/file-upload.js");
const ElementMeasurementPort = require("./Ports/element-measurement.js");
const DropdownPort = require("./Ports/dropdown.js");
const TextareaAutoResizePort = require("./Ports/textarea-auto-resize.js");
const FilePreviewMediaPort = require("./Ports/file-preview-media.js");
const DocxPreviewPort = require("./Ports/docx-preview.js");
const SpreadsheetPreviewPort = require("./Ports/spreadsheet-preview.js");
const SafeHtmlPort = require("./Ports/safe-html.js");
const ClipboardPort = require("./Ports/clipboard.js");
const MeticulousPort = require("./Ports/meticulous.js");
const BottomSheetSwipePort = require("./Ports/bottomSheetSwipe.js");
const sentryDsn = `https://${CONFIGURATION.sentryKey}@${CONFIGURATION.sentryOrganizationId}.ingest.sentry.io/${CONFIGURATION.sentryProjectId}`;
Sentry.init({
  dsn: sentryDsn,
  environment: CONFIGURATION.sentryEnvironment,
  release: CONFIGURATION.sentryReleaseVersion,
  maxValueLength: 1000,
  ignoreErrors: ["Cannot resolve iframe with unknown index"],
  denyUrls: [
    /extensions\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari(-web)?-extension:\/\//i,
    // Safari reports extension script URLs as webkit-masked-url://hidden/
    /^webkit-masked-url:/i,
  ],
});
Sentry.setExtra("api_version", CONFIGURATION.apiVersion);

// Initial data passed to Elm (should match `Flags` defined in `Shared.elm`)
// https://guide.elm-lang.org/interop/flags.html
var flags = {
  locationHref: location.href,
  accountToken: AccountTokenStorage.getItem("accountToken") || null,
  bustCacheRandom: Math.random().toString(36).slice(2),
  accountPracticeId: LocalStorage.getItem("accountPracticeId"),
  apiUrl: CONFIGURATION.apiUrl,
  apiVersion: CONFIGURATION.apiVersion || null,
  emrNotesUrl: CONFIGURATION.emrNotesUrl,
  emrNotesVersion: CONFIGURATION.emrNotesVersion || null,
  emrCalendarsUrl: CONFIGURATION.emrCalendarsUrl,
  emrCalendarsVersion: CONFIGURATION.emrCalendarsVersion || null,
  emrMessagingUrl: CONFIGURATION.emrMessagingUrl,
  emrMessagingVersion: CONFIGURATION.emrMessagingVersion || null,
  release: CONFIGURATION.release || null,
  currentTime: new Date().getTime(),
  language: LocalStorage.getItem("hint-language") || navigator.language,
  hintEnv: CONFIGURATION.hintEnv || null,
  applePaySupported: !!(window.ApplePaySession && window.ApplePaySession.canMakePayments()),
  replayingRecordedSession: isReplayingRecordedSession,
};

// Start our Elm application
const app = Elm.Main.init({
  node: document.getElementById("elm-main"),
  flags: flags,
});

// Ports go here
// https://guide.elm-lang.org/interop/ports.html
WindowPort.add(app);
FullStoryPort.add(app, CONFIGURATION.fullStoryOrgId);
GooglePlacesPort.add(app, CONFIGURATION.googleMapsKey);
if (isReplayingRecordedSession) {
  GoogleRecaptchaReplayPort.add(app);
} else {
  GoogleRecaptchaPort.add(app);
}
LocalStorage.add(app);
NavigationPort.add(app);
SentryPort.add(app, CONFIGURATION.env);
StripePort.add(app, CONFIGURATION.stripePublishableKey);
ThemePort.add(app);
CustomJavascriptPort.add(app);
PageTitlePort.add(app);
PageFocusPort.add(app);
FocusElementPort.add(app);
DocumentLanguagePort.add(app);
RainforestPort.add(app);
OnBeforeUnloadPort.add(app);
OnTabSelectPort.add(app);
IntercomPort.add(app, CONFIGURATION.intercomAppId);
FaviconPort.add(app);
IdleTimePort.add(app);
ScrollDetectionPort.add(app);
FileSelectionPort.initFileSelection(app);
FileUploadPort.add(app);
ElementMeasurementPort.add(app);
AccountTokenStorage.add(app);
DropdownPort.add(app);
TextareaAutoResizePort.add(app);
FilePreviewMediaPort.add(app);
DocxPreviewPort.add(app);
SpreadsheetPreviewPort.add(app);
XmlPreviewPort.add(app);
PdfPreviewPort.add(app);
SafeHtmlPort.add();
ClipboardPort.add(app);
MeticulousPort.add(app);
BottomSheetSwipePort.add();

if (CONFIGURATION.statuspageEmbedCode) {
  window.setupStatusPage(CONFIGURATION.statuspageEmbedCode);
}

if (
  CONFIGURATION.newRelicAccountId &&
  CONFIGURATION.newRelicLicenseKey &&
  CONFIGURATION.newRelicApplicationId &&
  typeof setupNewRelic !== "undefined"
) {
  setupNewRelic(CONFIGURATION.newRelicAccountId, CONFIGURATION.newRelicLicenseKey, CONFIGURATION.newRelicApplicationId);
}
